import type { ProviderAuthSession } from "@cmmd-forge/contracts";

export interface ProviderAuthFailureTelemetry {
  readonly error: Error;
  readonly context: Readonly<Record<string, string>>;
}

const PROCESS_EXIT_MESSAGE_PATTERN = /^Sign-in exited with code (\d+)\.$/u;

/**
 * Build a renderer-side Sentry event for a terminal provider-auth failure.
 *
 * Session messages are deliberately excluded from the event: a CLI transcript
 * can contain a one-time device code or authorization URL. The server exposes
 * only constrained diagnostics (`authErrorCode` and `upstreamRequestId`) for
 * this purpose.
 */
export function getProviderAuthFailureTelemetry(
  session: ProviderAuthSession | null,
): ProviderAuthFailureTelemetry | null {
  if (session?.state !== "failed") return null;

  const exitCode = session.message?.match(PROCESS_EXIT_MESSAGE_PATTERN)?.[1];
  const failureKind = exitCode ? "process_exit" : (session.issue ?? "unknown");
  return {
    error: new Error("Provider authentication failed."),
    context: {
      auth_failure_kind: failureKind,
      ...(exitCode ? { auth_process_exit_code: exitCode } : {}),
      ...(session.authErrorCode ? { auth_error_code: session.authErrorCode } : {}),
      ...(session.upstreamRequestId ? { upstream_request_id: session.upstreamRequestId } : {}),
      provider: session.provider,
      surface: "provider_auth",
    },
  };
}
